/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: AI 智能看图（标签版）页面
 * @LastEditors: codex
 * @LastEditTime: 2026-03-16
 */
import './index.scss'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Card, DatePicker, Input, message, Select, Spin } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import CONFIG from '../../constants/config'
import { useAppSelector } from '../../app/hooks'
import useAITaskEvent, {
  PhotoDetailOutput,
  SearchOutput,
  TagGroup
} from './hooks/useAITaskEvent'
import PhotoDetailDialog from './components/PhotoDetailDialog'
import iconPlay from '../../images/icon_play.png'
// antd 使用 Dayjs 类型替代 RangePickerValue
type RangePickerValue = [Dayjs | null, Dayjs | null] | null

const { RangePicker } = DatePicker
const { Option, OptGroup } = Select

interface SearchFormState {
  keyword: string
  selectedTags: string[]
  dateRange: RangePickerValue | null
  mediaType: number
}

const DEFAULT_PAGE_SIZE = 5

/** 默认时间范围：最近 30 天 */
const getDefaultDateRange = (): [Dayjs, Dayjs] => [
  dayjs().subtract(30, 'days').startOf('day'),
  dayjs().endOf('day')
]

const INITIAL_FORM: SearchFormState = {
  keyword: '',
  selectedTags: [],
  dateRange: getDefaultDateRange(),
  mediaType: 0
}

const GroupPhotoPreview: React.FC = () => {
  const navigate = useNavigate()
  const groupId = useAppSelector((state) => state.group.groupId)
  const aiPhotoTagsState = useAppSelector((state) => state.group.aiPhotoTags)
  const { syncSearch, syncPhotoDetail } = useAITaskEvent()
  const [tagGroups, setTagGroups] = useState<TagGroup[]>([])
  const [searchForm, setSearchForm] = useState<SearchFormState>(INITIAL_FORM)
  const [loading, setLoading] = useState(false)
  const [photoList, setPhotoList] = useState<SearchOutput['data']['photos']>([])
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [detailVisible, setDetailVisible] = useState(false)
  const [currentPhoto, setCurrentPhoto] = useState<
    PhotoDetailOutput['data'] | null
  >(null)
  const [loadingPhotoSeq, setLoadingPhotoSeq] = useState<number | null>(null)
  const initializedFromCacheRef = useRef(false)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const cachedTagsMeta = useMemo(() => {
    if (!aiPhotoTagsState) {
      return { groupId: '', loaded: false, tags: [] }
    }
    const cachedGroupId = aiPhotoTagsState.groupId
    const cachedLoaded = aiPhotoTagsState.loaded
    const cachedTags = aiPhotoTagsState.tags
    const tagsArray = cachedTags || []
    return {
      groupId: cachedGroupId,
      loaded: cachedLoaded,
      tags: tagsArray
    }
  }, [aiPhotoTagsState])

  // formOverride 用于 reset 场景：state 异步更新，需直接传入新表单值
  const handleSearch = useCallback(
    async (
      targetPage?: number,
      groupIdOverride?: string,
      append?: boolean,
      formOverride?: SearchFormState
    ) => {
      const pageToUse = targetPage ?? page
      const formToUse = formOverride ?? searchForm
      const groupIdValue = (groupIdOverride || groupId || '').trim()
      if (!groupIdValue) {
        message.warning('请先选择团队')
        return
      }
      setLoading(true)

      try {
        const dateRange = formToUse.dateRange
        const hasDateRange =
          Array.isArray(dateRange) && dateRange[0] && dateRange[1]
        const response = await syncSearch({
          group_id: groupIdValue,
          page: pageToUse,
          page_size: DEFAULT_PAGE_SIZE,
          keyword: formToUse.keyword.trim() || undefined,
          selected_tags:
            formToUse.selectedTags.length > 0
              ? formToUse.selectedTags
              : undefined,
          tag_logic: 'AND',
          date_range: hasDateRange
            ? [
                (dateRange[0] as Dayjs).format('YYYY-MM-DD'),
                (dateRange[1] as Dayjs).format('YYYY-MM-DD')
              ]
            : undefined,
          media_type: formToUse.mediaType
        })

        const status = response.data?.status
        if (status && status !== 200) {
          message.error(response.data?.msg || '搜索失败')
          return
        }

        if (response.output?.success) {
          const newPhotos = response.output.data?.photos || []
          const newTotal = response.output.data?.total ?? 0
          if (append) {
            setPhotoList((prev) => [...prev, ...newPhotos])
            setTotal(newTotal)
            setPage(pageToUse)
          } else {
            setPhotoList(newPhotos)
            setTotal(newTotal)
            setPage(pageToUse)
          }
        } else {
          message.error(response.output?.message || '搜索失败')
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : '搜索失败'
        message.error(errMsg)
      } finally {
        setLoading(false)
      }
    },
    [groupId, page, searchForm, syncSearch]
  )

  const handleReset = useCallback(() => {
    const resetForm: SearchFormState = {
      keyword: '',
      selectedTags: [],
      dateRange: getDefaultDateRange(),
      mediaType: 0
    }
    setSearchForm(resetForm)
    setPage(1)
    // 传入 resetForm 避免 state 异步导致搜索仍用旧值
    handleSearch(1, undefined, false, resetForm)
  }, [handleSearch])

  const loadMore = useCallback(() => {
    if (loading || photoList.length >= total || total === 0) return
    handleSearch(page + 1, undefined, true)
  }, [loading, photoList.length, total, page, handleSearch])

  React.useEffect(() => {
    const sentinel = loadMoreSentinelRef.current
    const root = scrollContainerRef.current
    if (!sentinel || !root) return
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (
          !entry?.isIntersecting ||
          loading ||
          photoList.length >= total ||
          total === 0
        )
          return
        loadMore()
      },
      { root, rootMargin: '100px', threshold: 0 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loading, photoList.length, total, loadMore])

  const handlePhotoClick = useCallback(
    async (photo) => {
      if (loadingPhotoSeq !== null) return
      setLoadingPhotoSeq(photo.photo_seq)
      try {
        const response = await syncPhotoDetail({
          group_id: photo.group_id,
          photo_seq: photo.photo_seq
        })
        const status = response.data?.status
        if (status && status !== 200) {
          message.error(response.data?.msg || '获取详情失败')
          return
        }

        if (response.output?.success) {
          setCurrentPhoto(response.output.data)
          setDetailVisible(true)
        } else {
          message.error(response.output?.message || '获取详情失败')
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : '获取详情失败'
        message.error(errMsg)
      } finally {
        setLoadingPhotoSeq(null)
      }
    },
    [syncPhotoDetail, loadingPhotoSeq]
  )

  React.useEffect(() => {
    initializedFromCacheRef.current = false
  }, [groupId])

  React.useEffect(() => {
    if (initializedFromCacheRef.current) return
    if (!cachedTagsMeta.loaded || cachedTagsMeta.groupId !== groupId) {
      return
    }
    initializedFromCacheRef.current = true
    // 当前团队无标签数据，切换到旧版 AI 看图页
    if (cachedTagsMeta.tags.length === 0) {
      navigate('/photo/ai', { replace: true })
      return
    }
    setTagGroups(cachedTagsMeta.tags)
    setPage(1)
    handleSearch(1, groupId)
  }, [cachedTagsMeta, groupId, handleSearch, navigate])

  const EmptyIcon = () => (
    <svg
      className="empty-icon"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect
        x="8"
        y="12"
        width="48"
        height="40"
        rx="4"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="24" cy="26" r="6" stroke="currentColor" strokeWidth="2" />
      <path
        d="M8 44l14-14 10 10 16-16 8 8v12H8V44z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  )

  return (
    <div className="AIPhotoTags" role="main">
      <div className="group-photo-preview-page">
        <Card className="search-card">
          <h2 className="search-form-title" id="search-form-heading">
            筛选条件
          </h2>
          <div
            className="search-form"
            role="search"
            aria-labelledby="search-form-heading"
          >
            <div className="form-item">
              <label className="form-label" htmlFor="aipt-keyword">
                关键词
              </label>
              <Input
                id="aipt-keyword"
                value={searchForm.keyword}
                placeholder="搜索照片内容"
                onChange={(e) => {
                  const value = e.target.value
                  setSearchForm((prev) => ({ ...prev, keyword: value }))
                }}
                onPressEnter={() => handleSearch(1)}
                style={{ width: 200 }}
                allowClear
                aria-label="关键词搜索"
              />
            </div>
            <div className="form-item">
              <label className="form-label" id="aipt-tags-label">
                标签筛选
              </label>
              <Select
                mode="multiple"
                value={searchForm.selectedTags}
                placeholder="选择标签"
                onChange={(value) =>
                  setSearchForm((prev) => ({
                    ...prev,
                    selectedTags: value as string[]
                  }))
                }
                style={{ width: 320 }}
                aria-labelledby="aipt-tags-label"
              >
                {tagGroups.map((group) => (
                  <OptGroup key={group.type} label={group.type}>
                    {group.values.map((tag) => (
                      <Option
                        key={`${group.type}:${tag.value}`}
                        value={`${group.type}:${tag.value}`}
                      >
                        {tag.value}
                      </Option>
                    ))}
                  </OptGroup>
                ))}
              </Select>
            </div>
            <div className="form-item">
              <label className="form-label" id="aipt-date-label">
                时间范围
              </label>
              <RangePicker
                value={searchForm.dateRange || undefined}
                onChange={(dates) =>
                  setSearchForm((prev) => ({
                    ...prev,
                    dateRange: dates || null
                  }))
                }
                allowClear
                style={{ width: 280 }}
                aria-labelledby="aipt-date-label"
              />
            </div>
            <div className="form-item">
              <label className="form-label" id="aipt-media-label">
                媒体类型
              </label>
              <Select
                value={searchForm.mediaType}
                onChange={(value) =>
                  setSearchForm((prev) => ({
                    ...prev,
                    mediaType: value
                  }))
                }
                style={{ width: 120 }}
                aria-labelledby="aipt-media-label"
              >
                <Option value={0}>全部</Option>
                <Option value={1}>照片</Option>
                <Option value={2}>视频</Option>
              </Select>
            </div>
            <div className="form-item">
              <div className="form-actions">
                <Button type="primary" onClick={() => handleSearch(1)}>
                  搜索
                </Button>
                <Button onClick={handleReset}>重置</Button>
              </div>
            </div>
          </div>
        </Card>

        <div className="photo-wall-container" ref={scrollContainerRef}>
          <Spin spinning={loading}>
            {total > 0 && <div className="results-count">共 {total} 张</div>}
            <div className="photo-grid">
              {photoList.map((photo) => {
                const isVideo = photo.media_type === 2
                const isLoadingThis = loadingPhotoSeq === photo.photo_seq
                return (
                  <div
                    key={photo.photo_seq}
                    className={`photo-card${
                      isLoadingThis ? ' photo-card--loading' : ''
                    }`}
                    onClick={() => handlePhotoClick(photo)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handlePhotoClick(photo)
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`查看照片${
                      photo.photo_content ? `：${photo.photo_content}` : ''
                    }`}
                    aria-busy={isLoadingThis}
                  >
                    <div className="photo-card-content">
                      <div
                        className="photo-card-thumb"
                        style={{
                          backgroundImage: `url(${photo.thumbnail_url})`,
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'contain',
                          backgroundPosition: 'center'
                        }}
                      >
                        {isVideo && (
                          <img
                            className="photo-card-play-icon"
                            src={iconPlay}
                            alt=""
                            aria-hidden
                          />
                        )}
                        {isLoadingThis && (
                          <div className="photo-card-loading-mask">
                            <Spin size="small" />
                          </div>
                        )}
                      </div>
                      <p className="photo-card-info">
                        {photo.photo_time
                          ? dayjs(photo.photo_time).year() === dayjs().year()
                            ? dayjs(photo.photo_time).format(
                                CONFIG.photoTimeThisYearFormat
                              )
                            : dayjs(photo.photo_time).format(
                                CONFIG.photoTimeFormat
                              )
                          : '-'}
                      </p>
                      <p
                        className="photo-card-info photo-card-desc"
                        title={photo.photo_content}
                      >
                        {photo.photo_content || '-'}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
            {!loading && photoList.length === 0 && (
              <div className="empty-wrapper">
                <EmptyIcon />
                <div className="empty-title">暂无照片</div>
                <div className="empty-desc">
                  调整筛选条件后重新搜索，或确认当前团队下是否有已同步的 AI
                  标签数据
                </div>
              </div>
            )}
            {total > 0 && photoList.length < total && (
              <div
                ref={loadMoreSentinelRef}
                className="load-more-sentinel"
                aria-hidden
              />
            )}
          </Spin>
        </div>

        <PhotoDetailDialog
          visible={detailVisible}
          photo={currentPhoto}
          onClose={() => setDetailVisible(false)}
        />
      </div>
    </div>
  )
}

export default GroupPhotoPreview
